import React, { Component, ErrorInfo, ReactNode } from "react";
import { RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  isChunkError: boolean;
}

/**
 * Error Boundary для обработки ошибок загрузки чанков (ChunkLoadError)
 * Возникает когда пользователь пытается загрузить lazy-компонент после деплоя
 */
export class ChunkErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Проверяем, является ли ошибка ChunkLoadError
    const isChunkError =
      error.name === "ChunkLoadError" ||
      error.message.includes("Loading chunk") ||
      error.message.includes("Failed to fetch dynamically imported module") ||
      error.message.includes("Importing a module script failed");

    return { hasError: true, isChunkError };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("ChunkErrorBoundary caught error:", error, errorInfo);

    // Если это ошибка загрузки чанка - автоматически перезагружаем через 1 секунду
    if (this.state.isChunkError) {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4 p-6 text-center">
              <RefreshCw className="w-10 h-10 text-sequence-primary animate-spin" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Обновление приложения
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Доступна новая версия. Страница перезагрузится
                  автоматически...
                </p>
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-sequence-primary text-white rounded-lg hover:bg-sequence-dark transition-colors"
                >
                  Обновить сейчас
                </button>
              </div>
            </div>
          </div>
        );
      }

      // Для других ошибок показываем общее сообщение
      return (
        this.props.fallback || (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-4 p-6 text-center">
              <div className="text-red-500 text-4xl">!</div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">
                  Произошла ошибка
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  Попробуйте обновить страницу
                </p>
                <button
                  onClick={this.handleReload}
                  className="px-4 py-2 bg-sequence-primary text-white rounded-lg hover:bg-sequence-dark transition-colors"
                >
                  Обновить страницу
                </button>
              </div>
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}
